import React, { useEffect, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { groupBy, sortBy } from "lodash";
import { ResponsiveBar } from "@nivo/bar";

import * as codeActions from "../../../actions/codeActions";
import * as supportTicketActions from "../../../actions/supportTicketActions";
import * as customerActions from "../../../actions/customerActions";
import * as departmentActions from "../../../actions/departmentActions";
import * as userActions from "../../../actions/userActions";
import { getDifferenceBetweenDates } from "../../../helpers/date";
import SupportTicketsFilters from "./SupportTicketsFilters";
import HeaderWrapper from "../../common/HeaderWrapper";
import Loader from "../../common/Loader";
import "./SupportTicketsWidget.scss";

const INITIAL_FILTER_STATE = {
  customer: null,
  department: null,
  multi_assigned_to: null
};

const SupportTicketsWidget = ({
  actions,
  codes,
  customers,
  departments,
  users,
  history,
  auth
}) => {
  const [chartData, setChartData] = useState([]);
  const [filterState, setFilterState] = useState(INITIAL_FILTER_STATE);
  const [loading, setLoading] = useState(true);
  const [userOptions, setUserOptions] = useState([]);
  const fetchData = useCallback(
    async (newFilters = filterState) => {
      setLoading(true);
      const inProcessCode = codes.find(
        (code) =>
          code.entity === "Support Ticket" &&
          code.field === "status" &&
          code.value === "IP"
      );
      const onHoldCode = codes.find(
        (code) =>
          code.entity === "Support Ticket" &&
          code.field === "status" &&
          code.value === "H"
      );
      const payload = {
        filtered: {
          request_status: [
            { value: inProcessCode._id },
            { value: onHoldCode._id }
          ]
        }
      };
      if (newFilters?.customer?.length) {
        payload.filtered.customer = newFilters.customer;
      }
      if (newFilters?.department?.length) {
        payload.filtered.departmentUsers = userOptions.map((user) => ({
          value: user._id,
          label: user.full_name
        }));
      }
      if (newFilters?.multi_assigned_to?.length) {
        payload.filtered.multi_assigned_to = newFilters.multi_assigned_to;
      }
      if (newFilters.arrival_date && newFilters.completed_date) {
        payload.filtered.arrival_date = [
          newFilters.arrival_date,
          newFilters.completed_date
        ];
      }
      const { supportTickets } = await actions.loadSupportTickets(payload);
      const formattedTickets = supportTickets.map((ticket) => ({
        ...ticket,
        days: getDifferenceBetweenDates(
          new Date(ticket.created_on),
          new Date(),
          true
        )
      }));
      const groupedTickets = groupBy(formattedTickets, (x) =>
        x.days >= 5 ? "5+" : String(x.days)
      );
      const days = ["0", "1", "2", "3", "4", "5+"];
      const mappedInfo = days.map((day) => {
        const groupedCalls = groupBy(
          groupedTickets[day] || [],
          "request_status.label"
        );
        const inProcessCalls = sortBy(groupedCalls["In Process"], "created_on");
        const onHoldCalls = sortBy(groupedCalls["On Hold"], "created_on");
        const firstInProcessCall =
          Array.isArray(inProcessCalls) && inProcessCalls.length
            ? inProcessCalls[0]
            : null;
        const firstOnHoldCall =
          Array.isArray(onHoldCalls) && onHoldCalls.length
            ? onHoldCalls[0]
            : null;

        const lastInProcessCall =
          Array.isArray(inProcessCalls) && inProcessCalls.length
            ? inProcessCalls[inProcessCalls.length - 1]
            : null;
        const lastOnHoldCall =
          Array.isArray(onHoldCalls) && onHoldCalls.length
            ? onHoldCalls[onHoldCalls.length - 1]
            : null;

        return {
          index: day,
          "In Process": inProcessCalls?.length || 0,
          "On Hold": onHoldCalls?.length || 0,
          firstInProcessDate: firstInProcessCall?.created_on || null,
          firstOnHoldDate: firstOnHoldCall?.created_on || null,
          lastInProcessDate: lastInProcessCall?.created_on || null,
          lastOnHoldDate: lastOnHoldCall?.created_on || null
        };
      });
      setChartData(mappedInfo);
      setLoading(false);
    },
    [actions, codes, filterState, userOptions]
  );

  useEffect(() => {
    if (!codes.length) actions.loadCodes();
    if (!customers.length) actions.loadCustomers();
    if (!departments.length) actions.loadDepartments();
    if (!users.length) actions.loadUsers();
  }, [actions, codes, customers, departments, users]);

  useEffect(() => {
    if (codes.length) {
      fetchData();
    }
  }, [codes, fetchData]);

  useEffect(() => {
    if (!userOptions.length) {
      setUserOptions(users);
    }
  }, [users, userOptions]);

  const handleChangeFilters = (field, value) => {
    if (field === "department") {
      const filteredUsers = [];
      for (let i = 0; i < value.length; i += 1) {
        users.forEach((user) => {
          if (user.department._id === value[i].value) {
            filteredUsers.push(user);
          }
        });
      }
      setUserOptions(filteredUsers);
    }
    setFilterState((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangeFilterDate = (startDate, endDate) => {
    setFilterState((prev) => ({
      ...prev,
      arrival_date: startDate ? new Date(startDate) : null,
      completed_date: endDate ? new Date(endDate) : null
    }));
  };

  const handleResetFilters = () => {
    setFilterState(INITIAL_FILTER_STATE);
    fetchData(INITIAL_FILTER_STATE);
    setUserOptions(users);
  };

  const handleClickBar = ({ id, data }) => {
    const filters = {};
    if (id === "In Process") {
      const inProcessCode = codes.find(
        (code) =>
          code.entity === "Support Ticket" &&
          code.field === "status" &&
          code.value === "IP"
      );
      filters.request_status = [
        { label: inProcessCode.label, value: inProcessCode._id }
      ];
      filters.arrival_date = new Date(data.firstInProcessDate);
      filters.completed_date = new Date(data.lastInProcessDate);
    } else if (id === "On Hold") {
      const onHoldCode = codes.find(
        (code) =>
          code.entity === "Support Ticket" &&
          code.field === "status" &&
          code.value === "H"
      );
      filters.request_status = [
        { label: onHoldCode.label, value: onHoldCode._id }
      ];
      filters.arrival_date = new Date(data.firstOnHoldDate);
      filters.completed_date = new Date(data.lastOnHoldDate);
    }
    if (filterState.multi_assigned_to) {
      filters.multi_assigned_to = filterState.multi_assigned_to;
    }
    if (filterState.department) {
      filters.assigned_department = filterState.department;
    }
    if (filterState.customer) {
      filters.customer = filterState.customer;
    }
    localStorage.setItem("ticket-filters", JSON.stringify(filters));
    history.push("/app/support-tickets");
  };

  return (
    <React.Fragment>
      <HeaderWrapper breadcrumbs={[{ label: "Support Tickets" }]} />
      <div className="content-wrapper" style={{ height: 525 }}>
        {loading ? (
          <Loader loading />
        ) : (
          <div style={{ height: 400 }}>
            <SupportTicketsFilters
              customers={customers}
              departments={departments}
              filters={filterState}
              users={userOptions}
              onChange={handleChangeFilters}
              onChangeDate={handleChangeFilterDate}
              onFetch={() => fetchData(filterState)}
              onReset={handleResetFilters}
            />
            <ResponsiveBar
              data={chartData}
              keys={["In Process", "On Hold"]}
              indexBy="index"
              margin={{ top: 30, right: 130, bottom: 50, left: 60 }}
              padding={0.2}
              colorBy="indexValue"
              groupMode="grouped"
              borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
              axisTop={null}
              axisRight={null}
              onClick={handleClickBar}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "Days",
                legendPosition: "middle",
                legendOffset: 30
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "Tickets",
                legendPosition: "middle",
                legendOffset: -40,
                format: (e) => Math.floor(e) === e && e
              }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              legends={[
                {
                  dataFrom: "indexValue",
                  anchor: "bottom-right",
                  direction: "column",
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 2,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: "left-to-right",
                  itemOpacity: 0.85,
                  symbolSize: 20,
                  itemTextColor: auth.isNightMode ? "white" : "black",
                  effects: [
                    {
                      on: "hover",
                      style: {
                        itemOpacity: 1
                      }
                    }
                  ]
                }
              ]}
              animate
              motionStiffness={90}
              motionDamping={15}
              theme={{
                axis: {
                  ticks: {
                    text: {
                      fill: auth.isNightMode ? "white" : "black"
                    }
                  },
                  legend: {
                    text: {
                      fill: auth.isNightMode ? "white" : "black"
                    }
                  }
                }
              }}
              tooltip={(e) => (
                <div
                  style={{
                    padding: 10,
                    minWidth: 200,
                    width: "100%",
                    color: auth?.isNightMode ? "black" : "white",
                    backgroundColor: auth?.isNightMode
                      ? "rgba(255, 255, 255, 0.65)"
                      : "rgba(0, 0, 0, 0.65)",
                    backdropFilter: "blur(7px)",
                    borderRadius: 4,
                    textAlign: "center"
                  }}
                >
                  <div style={{ textAlign: "center", width: "100%" }}>
                    {e.indexValue >= 5 ? "5+" : e.indexValue || ""} day
                    {e.indexValue === "1" ? "" : "s"}
                  </div>
                  <hr
                    style={{
                      margin: 5
                    }}
                  />
                  <span>
                    {e.id || ""}: {e.value} ticket{e.value === 1 ? "" : "s"}
                  </span>
                </div>
              )}
              defs={[
                {
                  id: "redGradientA",
                  type: "linearGradient",
                  colors: [
                    { offset: 0, color: "#f8d3d3" },
                    { offset: 100, color: "#f1a7a7" }
                  ]
                },
                {
                  id: "redGradientB",
                  type: "linearGradient",
                  colors: [
                    { offset: 0, color: "#f1a7a7" },
                    { offset: 100, color: "#ea7b7b" }
                  ]
                },
                {
                  id: "redGradientC",
                  type: "linearGradient",
                  colors: [
                    { offset: 0, color: "#ea7b7b" },
                    { offset: 100, color: "#e25050" }
                  ]
                },
                {
                  id: "redGradientD",
                  type: "linearGradient",
                  colors: [
                    { offset: 0, color: "#e25050" },
                    { offset: 100, color: "#db2424" }
                  ]
                },
                {
                  id: "redGradientE",
                  type: "linearGradient",
                  colors: [
                    { offset: 0, color: "#db2424" },
                    { offset: 100, color: "#af1d1d" }
                  ]
                },
                {
                  id: "redGradientF",
                  type: "linearGradient",
                  colors: [
                    { offset: 0, color: "#af1d1d" },
                    { offset: 100, color: "#841515" }
                  ]
                },
                {
                  id: "blueGradient",
                  type: "linearGradient",
                  colors: [
                    { offset: 0, color: "#9dd0e2" },
                    { offset: 100, color: "#50accb" }
                  ]
                }
              ]}
              fill={[
                {
                  match: ({ data }) => data.id === "On Hold",
                  id: "blueGradient"
                },
                {
                  match: ({ data }) =>
                    data.index === 0 && data.id === "In Process",
                  id: "redGradientA"
                },
                {
                  match: ({ data }) =>
                    data.index === 1 && data.id === "In Process",
                  id: "redGradientB"
                },
                {
                  match: ({ data }) =>
                    data.index === 2 && data.id === "In Process",
                  id: "redGradientC"
                },
                {
                  match: ({ data }) =>
                    data.index === 3 && data.id === "In Process",
                  id: "redGradientD"
                },
                {
                  match: ({ data }) =>
                    data.index === 4 && data.id === "In Process",
                  id: "redGradientE"
                },
                {
                  match: ({ data }) =>
                    data.index === 5 && data.id === "In Process",
                  id: "redGradientF"
                }
              ]}
            />
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

SupportTicketsWidget.propTypes = {
  actions: PropTypes.object,
  codes: PropTypes.array,
  customers: PropTypes.array,
  departments: PropTypes.array,
  users: PropTypes.array,
  history: PropTypes.object,
  auth: PropTypes.object
};

function mapStatesToProps(state) {
  return {
    state: state.reducers,
    codes: state.reducers.codes,
    customers: state.reducers.customers,
    departments: state.reducers.departments,
    users: state.reducers.users,
    auth: state.reducers.auth
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...codeActions,
        ...supportTicketActions,
        ...customerActions,
        ...departmentActions,
        ...userActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(SupportTicketsWidget);
